import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import DocumentVaultModal from '../Modals/Planning/DocumentVaultModal';
import ExpenseTrackerModal from '../Modals/Planning/ExpenseTrackerModal';
import RSVPManagerModal from '../Modals/Planning/RSVPManagerModal';
import ProjectManagementModal from '../Modals/Planning/ProjectManagementModal';
import LocalProvidersModal from '../Modals/LocalProvidersModal';
import { useAuth } from '../../context/AuthContext';

const {
  FiCheckCircle, FiCircle, FiFileText, FiUsers, FiDollarSign,
  FiChevronRight, FiMapPin, FiShoppingBag, FiEdit3, FiShield, FiPhone, FiList,
  FiSettings, FiX, FiTrash2, FiPlus
} = FiIcons;

const INITIAL_TASKS = [
  {
    id: 1, title: 'Obtain Legal Death Certificate',
    category: 'Legal', actionLabel: 'View Legal Guide',
    action: 'nav', path: '/resources?tab=Legal',
    icon: FiFileText, completed: true,
  },
  {
    id: 2, title: 'Select Memorial Home or Service',
    category: 'Logistics', actionLabel: 'Find Local Providers',
    action: 'modal:providers',
    icon: FiMapPin, completed: false,
  },
  {
    id: 3, title: 'Choose Casket, Urn, or Memorial Box',
    category: 'Marketplace', actionLabel: 'Browse Selection',
    action: 'nav', path: '/marketplace',
    icon: FiShoppingBag, completed: false,
  },
  {
    id: 4, title: 'Write the Obituary or Life Story',
    category: 'Tribute', actionLabel: 'Start Writing Tool',
    action: 'nav', path: '/resources?tab=Workshops',
    icon: FiEdit3, completed: false,
  },
  {
    id: 5, title: 'Order Floral Arrangements & Sympathy Gifts',
    category: 'Marketplace', actionLabel: 'Shop Memorial Flowers',
    action: 'nav', path: '/marketplace',
    icon: FiShoppingBag, completed: false,
  },
  {
    id: 6, title: 'Order Souvenirs, Outfits & Accessories',
    category: 'Marketplace', actionLabel: 'Shop Memorial Items',
    action: 'nav', path: '/marketplace',
    icon: FiShoppingBag, completed: false,
  },
  {
    id: 7, title: 'Notify Social Security & Financial Institutions',
    category: 'Legal', actionLabel: 'Notification Checklist',
    action: 'nav', path: '/resources?tab=Legal',
    icon: FiShield, completed: false,
  },
];

/* ── Admin Task Editor Modal ────────────────────────────────────────────── */
const AdminTaskEditorModal = ({ isOpen, onClose, tasks, onSave }) => {
  const [localTasks, setLocalTasks] = useState([...tasks]);

  if (!isOpen) return null;

  const updateTask = (id, field, val) => {
    setLocalTasks(prev => prev.map(t => t.id === id ? { ...t, [field]: val } : t));
  };

  const removeTask = (id) => {
    setLocalTasks(prev => prev.filter(t => t.id !== id));
  };

  const addTask = () => {
    setLocalTasks(prev => [...prev, {
      id: Date.now(),
      title: 'New Action Item',
      category: 'General',
      actionLabel: 'View Details',
      action: 'nav',
      path: '/explore',
      icon: FiList,
      completed: false,
    }]);
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-accent-navy/60 backdrop-blur-md">
      <div className="bg-white rounded-[40px] shadow-2xl w-full max-w-3xl flex flex-col max-h-[90vh] overflow-hidden border border-brand-dark">
        <div className="p-8 border-b border-brand-light flex justify-between items-center bg-brand-light/30">
          <div>
            <h3 className="text-2xl font-serif text-accent-navy">Edit Immediate Actions</h3>
            <p className="text-[10px] font-black uppercase tracking-widest text-text-muted">Configure Legacy Planning Steps</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-brand-light rounded-full text-text-muted transition-colors">
            <SafeIcon icon={FiX} className="w-6 h-6" />
          </button>
        </div>
        
        <div className="p-8 overflow-y-auto space-y-4 custom-scrollbar">
          {localTasks.map((t, idx) => (
            <div key={t.id} className="flex flex-col sm:flex-row gap-4 p-5 border border-brand-dark rounded-2xl bg-white shadow-sm relative group">
              <div className="flex-1 space-y-3">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="space-y-1">
                     <label className="text-[10px] font-black uppercase tracking-widest text-accent-navy">Title</label>
                     <input value={t.title} onChange={e => updateTask(t.id, 'title', e.target.value)} 
                            className="w-full px-4 py-2.5 bg-brand-light border border-brand-dark rounded-xl text-sm outline-none focus:border-accent-gold transition-colors" />
                  </div>
                  <div className="space-y-1">
                     <label className="text-[10px] font-black uppercase tracking-widest text-accent-navy">Category Tag</label>
                     <input value={t.category} onChange={e => updateTask(t.id, 'category', e.target.value)} 
                            className="w-full px-4 py-2.5 bg-brand-light border border-brand-dark rounded-xl text-sm outline-none focus:border-accent-gold transition-colors" />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="space-y-1">
                     <label className="text-[10px] font-black uppercase tracking-widest text-accent-navy">Action Label</label>
                     <input value={t.actionLabel} onChange={e => updateTask(t.id, 'actionLabel', e.target.value)} 
                            className="w-full px-4 py-2.5 bg-brand-light border border-brand-dark rounded-xl text-sm outline-none focus:border-accent-gold transition-colors" />
                  </div>
                  <div className="space-y-1">
                     <label className="text-[10px] font-black uppercase tracking-widest text-accent-navy">Action Type</label>
                     <select value={t.action} onChange={e => updateTask(t.id, 'action', e.target.value)} 
                             className="w-full px-4 py-2.5 bg-brand-light border border-brand-dark rounded-xl text-sm outline-none focus:border-accent-gold transition-colors">
                       <option value="nav">Navigation Link</option>
                       <option value="modal:providers">Open Providers Modal</option>
                       <option value="modal:vault">Open Vault Modal</option>
                     </select>
                  </div>
                </div>
                {t.action === 'nav' && (
                  <div className="space-y-1">
                    <label className="text-[10px] font-black uppercase tracking-widest text-accent-navy">Path / URL</label>
                    <input value={t.path || ''} onChange={e => updateTask(t.id, 'path', e.target.value)} 
                           className="w-full px-4 py-2.5 bg-brand-light border border-brand-dark rounded-xl text-sm outline-none focus:border-accent-gold transition-colors" />
                  </div>
                )}
              </div>
              <button title="Remove Task" onClick={() => removeTask(t.id)} 
                      className="absolute top-4 right-4 p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors opacity-0 group-hover:opacity-100">
                <SafeIcon icon={FiTrash2} className="w-5 h-5" />
              </button>
            </div>
          ))}
          
          <button onClick={addTask} 
                  className="w-full py-4 border-2 border-dashed border-brand-dark rounded-2xl text-accent-navy font-bold hover:bg-brand-light hover:border-accent-gold transition-all flex items-center justify-center gap-2 text-sm shadow-sm">
            <SafeIcon icon={FiPlus} /> Add New Task
          </button>
        </div>
        
        <div className="p-6 border-t border-brand-light flex justify-end gap-3 bg-brand-light/20">
          <button onClick={onClose} 
                  className="px-6 py-3 border border-brand-dark rounded-xl text-[10px] font-black uppercase tracking-widest text-text-muted hover:bg-brand-light transition-colors">
            Cancel
          </button>
          <button onClick={() => onSave(localTasks)} 
                  className="px-6 py-3 bg-accent-gold text-accent-navy rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-accent-navy hover:text-white transition-all shadow-md">
            Save Changes
          </button>
        </div>
      </div>
    </div>
  );
};

/* ── Sidebar tool button ──────────────────────────────────────────────── */
const VaultItem = ({ icon, title, desc, onClick }) => (
  <button onClick={onClick}
    className="flex gap-4 group text-left w-full hover:bg-white/5 p-3 -m-3 rounded-2xl transition-all">
    <div className="w-10 h-10 bg-white/10 rounded-xl flex items-center justify-center
                    group-hover:bg-accent-gold transition-all flex-shrink-0 border border-white/10">
      <SafeIcon icon={icon} className="w-5 h-5" />
    </div>
    <div>
      <h5 className="text-sm font-bold group-hover:text-accent-gold transition-colors">{title}</h5>
      <p className="text-[10px] text-white/50 font-light leading-tight">{desc}</p>
    </div>
  </button>
);

/* ── Main Component ───────────────────────────────────────────────────── */
const FuneralPlanning = () => {
  const navigate = useNavigate();
  const { permissions } = useAuth();
  
  // Try to load tasks from local storage or fallback to default
  const [tasks, setTasks] = useState(() => {
    const saved = localStorage.getItem('funeral_planning_tasks');
    return saved ? JSON.parse(saved) : INITIAL_TASKS;
  });
  
  const [activeModal, setActive] = useState(null);
  const [isEditorOpen, setIsEditorOpen] = useState(false);

  const isAdmin = permissions?.isGlobalAdmin?.();

  const toggleTask = id =>
    setTasks(prev => prev.map(t => t.id === id ? { ...t, completed: !t.completed } : t));

  const handleAction = task => {
    if (task.action === 'modal:providers') { setActive('providers'); return; }
    if (task.action === 'modal:vault') { setActive('vault'); return; }
    if (task.path) navigate(task.path);
  };

  const handleSaveTasks = (newTasks) => {
    setTasks(newTasks);
    localStorage.setItem('funeral_planning_tasks', JSON.stringify(newTasks));
    setIsEditorOpen(false);
  };

  const completedCount = tasks.filter(t => t.completed).length;
  const progress       = tasks.length > 0 ? Math.round((completedCount / tasks.length) * 100) : 0;

  return (
    <div className="space-y-12">
      
      {/* Admin Mode Banner */}
      {isAdmin && (
        <div className="bg-brand-light border border-brand-dark rounded-2xl flex flex-col sm:flex-row items-start sm:items-center justify-between p-4 mb-2 shadow-sm gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-accent-navy text-white rounded-xl flex items-center justify-center shrink-0">
              <SafeIcon icon={FiSettings} className="w-5 h-5" />
            </div>
            <div>
              <p className="font-bold text-accent-navy text-sm uppercase tracking-wider">Admin Mode Active</p>
              <p className="text-xs text-text-muted font-light mt-0.5">Manage articles, videos, featured content and layout</p>
            </div>
          </div>
          <button onClick={() => setIsEditorOpen(true)} 
                  className="px-6 py-3 w-full sm:w-auto bg-accent-navy text-white rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-accent-gold transition-colors flex items-center justify-center gap-2 shadow-md shrink-0">
            <SafeIcon icon={FiEdit3} /> Open Editor
          </button>
        </div>
      )}

      {/* Progress card */}
      <div className="bg-white p-8 md:p-12 rounded-[40px] border border-brand-dark shadow-sm">
        <div className="flex flex-col md:flex-row items-center justify-between gap-8 mb-10">
          <div>
            <h3 className="text-3xl font-serif text-accent-navy mb-2">Legacy Planning Suite</h3>
            <p className="text-text-muted font-light max-w-md">
              An exhaustive, actionable guide to honoring your loved one with precision and care.
            </p>
          </div>
          <div className="text-center md:text-right">
            <div className="text-5xl font-serif text-accent-gold mb-1">{progress}%</div>
            <p className="text-[10px] font-black uppercase tracking-widest text-text-muted">
              Arrangements Complete
            </p>
          </div>
        </div>
        <div className="h-4 bg-brand-light rounded-full overflow-hidden border border-brand-dark">
          <motion.div
            initial={{ width: 0 }} animate={{ width: `${progress}%` }}
            transition={{ duration: 0.6 }}
            className="h-full bg-accent-gold"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
        {/* Task list */}
        <div className="lg:col-span-2 space-y-4">
          <div className="flex items-center justify-between px-4 mb-2">
            <h4 className="text-[10px] font-black uppercase tracking-[0.2em] text-accent-navy">
              Immediate Actions
            </h4>
            <span className="text-[10px] font-bold text-text-muted">
              {completedCount} of {tasks.length} Completed
            </span>
          </div>

          {tasks.map((task, idx) => (
            <motion.div key={task.id}
              initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.05 }}
              className={`group p-6 rounded-[32px] border-2 transition-all flex items-center gap-6 ${
                task.completed
                  ? 'bg-brand-light border-brand-dark'
                  : 'bg-white border-brand-dark hover:border-accent-gold hover:shadow-xl'
              }`}
            >
              <button onClick={() => toggleTask(task.id)}
                className="flex-shrink-0 transition-transform active:scale-90">
                <SafeIcon
                  icon={task.completed ? FiCheckCircle : FiCircle}
                  className={`w-8 h-8 ${
                    task.completed ? 'text-green-500' : 'text-text-muted group-hover:text-accent-gold'
                  }`}
                />
              </button>

              <div className="flex-grow">
                <div className="flex items-center gap-2 mb-1">
                  <span className={`text-[9px] font-black uppercase tracking-widest px-2 py-0.5 rounded-md ${
                    task.completed ? 'bg-brand-dark/20 text-text-muted' : 'bg-accent-gold/10 text-accent-gold'
                  }`}>
                    {task.category}
                  </span>
                  {task.action === 'modal:providers' && !task.completed && (
                    <span className="text-[9px] font-black uppercase tracking-widest px-2 py-0.5
                                     rounded-md bg-blue-50 text-blue-500">
                      Directory
                    </span>
                  )}
                </div>
                <h4 className={`text-lg font-serif transition-all ${
                  task.completed ? 'text-text-muted line-through opacity-60' : 'text-accent-navy'
                }`}>
                  {task.title}
                </h4>
                <button
                  onClick={() => handleAction(task)}
                  className={`mt-2 inline-flex items-center gap-1.5 text-[10px] font-black uppercase
                              tracking-widest transition-all ${
                    task.completed
                      ? 'text-text-muted hover:text-accent-gold'
                      : 'text-accent-gold hover:translate-x-1'
                  }`}
                >
                  {task.actionLabel}
                  <SafeIcon icon={FiChevronRight} />
                </button>
              </div>

              <div className={`hidden sm:flex w-14 h-14 rounded-2xl items-center justify-center
                              transition-colors shadow-sm ${
                task.completed
                  ? 'bg-brand-dark/10 text-text-muted'
                  : 'bg-brand-light text-accent-navy group-hover:bg-accent-gold group-hover:text-white'
              }`}>
                <SafeIcon icon={task.icon || FiList} className="w-6 h-6" />
              </div>
            </motion.div>
          ))}
          
          {tasks.length === 0 && (
            <div className="text-center py-10 bg-brand-light rounded-3xl border border-brand-dark border-dashed">
              <p className="text-sm text-text-muted">No immediate actions configured.</p>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-8">
          <div className="bg-accent-navy text-white p-10 rounded-[40px] shadow-2xl relative overflow-hidden">
            <div className="absolute top-0 right-0 w-32 h-32 bg-accent-gold/10 rounded-full
                            -mr-16 -mt-16 blur-3xl" />
            <h4 className="font-serif text-2xl mb-8 relative z-10">Planning Tools</h4>
            <div className="space-y-6 relative z-10">
              <VaultItem onClick={() => setActive('vault')} icon={FiFileText}
                title="Document Vault" desc="Securely store death certificates & wills." />
              <VaultItem onClick={() => setActive('expense')} icon={FiDollarSign}
                title="Expense Tracker" desc="Monitor funeral costs in real-time." />
              <VaultItem onClick={() => setActive('rsvp')} icon={FiUsers}
                title="RSVP Manager" desc="Track guests for memorial services." />
              <VaultItem onClick={() => setActive('pm')} icon={FiList}
                title="Project Management" desc="Manage deadlines & critical to-do lists." />
            </div>
          </div>

          <div className="bg-white p-8 rounded-[40px] border border-brand-dark text-center group shadow-sm">
            <div className="w-16 h-16 bg-accent-gold/10 text-accent-gold rounded-3xl flex items-center
                            justify-center mx-auto mb-6 group-hover:scale-110 transition-transform
                            border border-accent-gold/20 shadow-sm">
              <SafeIcon icon={FiPhone} className="w-8 h-8" />
            </div>
            <h5 className="font-serif text-xl text-accent-navy mb-3">Concierge Support</h5>
            <p className="text-sm text-text-muted font-light mb-8 italic">
              "We handle the logistics so you can focus on your family."
            </p>
            <button className="w-full btn-primary py-4 rounded-2xl shadow-lg active:scale-95">
              Connect with an Expert
            </button>
          </div>
        </div>
      </div>

      {/* Editor Modal */}
      <AdminTaskEditorModal 
        isOpen={isEditorOpen} 
        onClose={() => setIsEditorOpen(false)} 
        tasks={tasks} 
        onSave={handleSaveTasks} 
      />

      {/* Planning Modals */}
      <DocumentVaultModal    isOpen={activeModal === 'vault'}     onClose={() => setActive(null)} />
      <ExpenseTrackerModal   isOpen={activeModal === 'expense'}   onClose={() => setActive(null)} />
      <RSVPManagerModal      isOpen={activeModal === 'rsvp'}      onClose={() => setActive(null)} />
      <ProjectManagementModal isOpen={activeModal === 'pm'}       onClose={() => setActive(null)} />
      <LocalProvidersModal   isOpen={activeModal === 'providers'} onClose={() => setActive(null)} />
    </div>
  );
};

export default FuneralPlanning;